import { useCallback } from 'react';

import type HighchartsType from 'highcharts/highstock';

import { DateTextVariants, useDateText } from '@/features/_shared/user';
import type { TimeSeriesPointCustom } from '@/features/charts/lib/buildChartLineSeries';
import { createTooltip } from '@/features/charts/lib/createTooltip';
import type { ChartAppearance } from '@/features/charts/theme/chartAppearance';

const createRolloverTooltipStyles = (appearance: ChartAppearance) => ({
  bodyRow:
    'font-size: 10px; font-style: normal;font-weight: 500;justify-content: space-between;height: 19px; display: flex;',
  container: 'display: flex; flex-direction: column; width:350px;',
  dateColumn: `width: 65px; padding-left: 8px; border-left: 1px solid ${appearance.border};`,
  headerRow: 'font-size: 8px; display: flex; font-weight: 400;justify-content: space-between;',
  nameColumn: `width: 152px; padding-right: 8px; border-right: 1px solid ${appearance.border}; text-overflow: ellipsis; overflow: hidden; white-space: nowrap;`,
  periodColumn: 'width: 65px; padding-left: 8px; padding-right: 8px;',
  valueBox:
    'font-size:10px; line-height:13px; height:14px; width:55px; padding-left:4px; padding-right:4px; text-align: start;',
  valueContainer: 'height 19px; padding-left:8px; padding-top:3px'
});

const ROLLOVER_TOOLTIP_COLUMN_WIDTHS = { date: 65, name: 152, period: 65 } as const;

type FullScreenRolloverFormatter = (
  allChartSeries: HighchartsType.Series[],
  hoveredPointXValue: string | number | undefined
) => string;

const createTooltipColumn = (content: string, style: string): string =>
  `<div style="${style}">${content}</div>`;

const createTooltipValueBox = ({
  backgroundColor,
  styles,
  textColor,
  value
}: {
  backgroundColor: string;
  styles: ReturnType<typeof createRolloverTooltipStyles>;
  textColor: string;
  value: string;
}): string =>
  `<div style="background-color:${backgroundColor}; color:${textColor}; ${styles.valueBox}">${value}</div>`;

const createRolloverTooltipHeader = (appearance: ChartAppearance): string => {
  const styles = createRolloverTooltipStyles(appearance);
  const columns = [
    { content: 'Short Title', style: `width: ${ROLLOVER_TOOLTIP_COLUMN_WIDTHS.name}px;` },
    { content: 'PeriodAbs', style: `width: ${ROLLOVER_TOOLTIP_COLUMN_WIDTHS.period}px;` },
    { content: 'Last date', style: `width: ${ROLLOVER_TOOLTIP_COLUMN_WIDTHS.date}px;` },
    { content: '', style: 'width: 10%;' }
  ]
    .map(({ content, style }) => createTooltipColumn(content, style))
    .join('');
  return `<div style="${styles.container}"><div style="${styles.headerRow}">${columns}</div>`;
};

const createRolloverTooltipBodyRow = ({
  appearance,
  custom,
  formatDateString,
  point,
  valueTextColor,
  value
}: {
  appearance: ChartAppearance;
  custom: TimeSeriesPointCustom | undefined;
  formatDateString: (date: string, variant: DateTextVariants) => string;
  point: HighchartsType.Point;
  valueTextColor: string;
  value: string;
}): string => {
  const styles = createRolloverTooltipStyles(appearance);
  const nameColumn = createTooltipColumn(point.series.name, styles.nameColumn);
  const periodColumn = createTooltipColumn(custom?.periodAbs ?? '', styles.periodColumn);
  const date = formatDateString(new Date(point.x).toJSON(), DateTextVariants.FULL);
  const dateColumn = createTooltipColumn(date, styles.dateColumn);
  const valueBox = createTooltipValueBox({
    backgroundColor:
      typeof point.series.color === 'string' ? point.series.color : appearance.foreground,
    styles,
    textColor: valueTextColor,
    value
  });
  const valueContainer = createTooltipColumn(valueBox, styles.valueContainer);
  return `<div style="${styles.bodyRow}">${nameColumn}${periodColumn}${dateColumn}${valueContainer}</div>`;
};

export const useFullScreenRolloverFormatter = (
  appearance: ChartAppearance
): FullScreenRolloverFormatter => {
  const { formatDateString } = useDateText();
  const valueTextColor = appearance.accentForeground;

  return useCallback(
    (allChartSeries, hoveredPointXValue) =>
      createTooltip({
        allChartSeries,
        hoveredPointXValue,
        createHeader: () => createRolloverTooltipHeader(appearance),
        createBodyRow: (point, custom, value) =>
          createRolloverTooltipBodyRow({
            appearance,
            custom,
            formatDateString,
            point,
            valueTextColor,
            value
          })
      }),
    [appearance, formatDateString, valueTextColor]
  );
};

export const createFullScreenRolloverTooltip = (
  createTooltipFormatter: FullScreenRolloverFormatter,
  appearance: ChartAppearance
): HighchartsType.TooltipOptions => ({
  backgroundColor: appearance.tooltipBackground,
  borderColor: appearance.border,
  borderRadius: 0,
  className: 'tooltip-fullscreen-chart',
  enabled: true,
  followPointer: true,
  formatter(): string {
    return createTooltipFormatter(this.series.chart.series, this.x);
  },
  positioner() {
    return { x: 100, y: 80 };
  },
  shape: 'rect',
  split: false,
  style: {
    color: appearance.tooltipForeground,
    fontFamily: appearance.fontFamily,
    zIndex: 1303
  },
  useHTML: true
});
